import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";

const COOKIE_CONSENT_KEY = "petrobell_cookie_consent";

type ConsentType = "all" | "necessary" | null;

const CookieBanner = () => {
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);

  const content = {
    title: language === 'sv' ? 'Vi använder cookies' : 'We use cookies',
    description: language === 'sv'
      ? 'Vi använder cookies för att förbättra din upplevelse på vår webbplats. Läs mer om hur vi behandlar dina personuppgifter i vår integritetspolicy.'
      : 'We use cookies to improve your experience on our website. Read more about how we process your personal data in our privacy policy.',
    acceptAll: language === 'sv' ? 'Acceptera alla' : 'Accept all',
    acceptNecessary: language === 'sv' ? 'Endast nödvändiga' : 'Necessary only',
    privacyPolicy: language === 'sv' ? 'Integritetspolicy' : 'Privacy Policy'
  };

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleConsent = (type: ConsentType) => {
    if (type) {
      localStorage.setItem(COOKIE_CONSENT_KEY, type);
      setShowBanner(false);
    }
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex p-3 bg-primary/10 rounded-xl">
                <Cookie className="h-6 w-6 text-primary" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {content.title}
                  </h3>
                  <button
                    onClick={() => handleConsent("necessary")}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Stäng"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {content.description}{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-primary hover:underline font-medium"
                  >
                    {content.privacyPolicy}
                  </Link>
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => handleConsent("all")}
                    className="bg-primary hover:bg-primary/90 text-white font-medium"
                  >
                    {content.acceptAll}
                  </Button>
                  <Button
                    onClick={() => handleConsent("necessary")}
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {content.acceptNecessary}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
